// utils/exportHelpers.js
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

/**
 * Capitaliza palabras ignorando preposiciones comunes.
 */
const capitalize = (text = "") =>
  text
    .toLowerCase()
    .split(" ")
    .map((word) =>
      ["de", "del", "la", "y"].includes(word)
        ? word
        : word.charAt(0).toUpperCase() + word.slice(1)
    )
    .join(" ");

/**
 * Formatea una cadena de cobertura (por ejemplo, "cdmx, edo. mex")
 */
const formatCobertura = (text = "") => {
  if (!text) return "Sin cobertura";
  return text
    .split(",")
    .map((t) => {
      const clean = t.trim().toLowerCase();
      return clean === "cdmx" ? "CDMX" : capitalize(clean);
    })
    .join(", ");
};

/**
 * Formatea el nombre de una empresa
 */
const formatEmpresa = (text = "") => {
  const excepciones = ["sa", "de", "cv", "c.v", "s.a", "s.a.", "y"];
  return text
    .split(" ")
    .map((word) => {
      const clean = word.toLowerCase().replace(/\./g, "");
      return excepciones.includes(clean)
        ? word.toLowerCase()
        : capitalize(word);
    })
    .join(" ");
};

/**
 * Exporta un array de empresas a Excel estilizado
 */
export const exportarProveedoresAExcel = async (rows) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Proveedores");

  worksheet.columns = [
    { header: "Empresa", key: "empresa", width: 30 },
    { header: "Nombre", key: "nombre", width: 25 },
    { header: "Cobertura", key: "cobertura", width: 20 },
    { header: "Capacidad", key: "capacidad", width: 15 },
    { header: "Instalaciones", key: "instalaciones", width: 20 },
    { header: "Especialidad", key: "especialidad", width: 30 },
    { header: "Estatus", key: "estatus", width: 20 },
  ];

  // Header styling
  const headerRow = worksheet.getRow(1);
  headerRow.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 12 };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF1D3557" },
    };
    cell.alignment = {
      horizontal: "center",
      vertical: "middle",
    };
    cell.border = {
      bottom: { style: "thin", color: { argb: "FFFFFFFF" } },
    };
  });

  rows.forEach((row, index) => {
    const item = row.original;

    const rowExcel = worksheet.addRow({
      empresa: formatEmpresa(item.razonSocial),
      nombre: capitalize(item.nombre),
      cobertura: formatCobertura(item.cobertura),
      capacidad: item.capacidadInstalaciones || 0,
      instalaciones: item.instalacionesActivas || 0,
      especialidad: (item.especialidadesSelec || [])
        .map((e) => capitalize(e.nombre))
        .join(", "),
      estatus: capitalize(item.estatusEmpresa?.nombre || "Inactivo"),
    });

    // Celdas
    rowExcel.eachCell((cell, colNumber) => {
      cell.font = {
        name: "Helvetica",
        size: 11,
        color: { argb: "FF223D51" },
      };
      cell.alignment = {
        horizontal: [1, 2, 3, 6].includes(colNumber) ? "left" : "center",
        vertical: "middle",
      };
    });

    // Zebra striping
    if (index % 2 === 1) {
      rowExcel.eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFEEEEEE" },
        };
      });
    }
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(blob, "Proveedores.xlsx");
};

//ExportartExcelInstaladores
export const exportarExcelInstaladores = async (data) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Instaladores");

  worksheet.columns = [
    { header: "Nombre completo", key: "nombreCompleto", width: 30 },
    { header: "Gafete", key: "gafete", width: 15 },
    { header: "Proveedor", key: "empresa", width: 35 }, // cambiamos a "Proveedor"
    { header: "Contrato", key: "contrato", width: 15 },
    { header: "REPSE", key: "repse", width: 15 },
    { header: "INE", key: "ine", width: 15 },
    { header: "IMSS", key: "imss", width: 15 },
    { header: "Cert. Médico", key: "certMedico", width: 15 },
    { header: "DC3", key: "dc3", width: 15 },
  ];

  const formatearFecha = (fecha) => {
    if (!fecha) return "Sin definir";
    const date = new Date(fecha);
    if (isNaN(date)) return "Sin definir";
    return `${String(date.getDate()).padStart(2, "0")}-${String(
      date.getMonth() + 1
    ).padStart(2, "0")}-${date.getFullYear()}`;
  };

  // Estilos del encabezado
  worksheet.getRow(1).eachCell((cell, colNumber) => {
    cell.font = { bold: true, size: 12, color: { argb: "FFFFFFFF" } };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF223D51" },
    };
    cell.alignment = {
      vertical: "middle",
      horizontal: colNumber === 1 || colNumber === 3 ? "left" : "center",
      wrapText: false,
    };
  });

  data.forEach((item) => {
    const row = worksheet.addRow({
      nombreCompleto: capitalize(`${item.nombre} ${item.apellidos}`),
      gafete: item.numGafete || "",
      empresa: formatEmpresa(item.empresa?.razonSocial || ""),
      contrato: formatearFecha(item.empresa?.contrato?.fechaVencimiento),
      repse: formatearFecha(item.empresa?.repse?.fechaVencimiento),
      ine: formatearFecha(item.fechaVencimientoIne),
      imss: formatearFecha(item.fechaVencimientoImss),
      certMedico: formatearFecha(item.fechaVencimientoCertificadoMedico),
      dc3: formatearFecha(item.fechaVencimientoDc3),
    });

    row.eachCell((cell, colNumber) => {
      cell.font = {
        name: "Helvetica",
        size: 11,
        color: { argb: "FF223D51" },
      };
      cell.alignment = {
        vertical: "middle",
        horizontal: colNumber === 1 || colNumber === 3 ? "left" : "center",
        wrapText: colNumber !== 3, // NO permitir wrap en empresa
      };
    });
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(blob, "Instaladores.xlsx");
};
